/**
 * Login & Security (Aug 2026 mobile parity).
 * Two blocks:
 *   • Two-factor auth (TOTP) — setup / verify / disable
 *   • Active sessions — list + revoke individual
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, ShieldCheck, Monitor, LogOut, Loader2, Copy, Check } from 'lucide-react';

export default function LoginSecurity() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [twofaSetup, setTwofaSetup] = useState(null); // {secret, otpauth_uri}
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(null);
  const [copied, setCopied] = useState(false);

  const loadSessions = () =>
    api.get('/auth/sessions')
      .then((r) => setSessions(r.data.sessions || []))
      .catch(() => setSessions([]));

  useEffect(() => { loadSessions(); }, []);

  const startSetup = async () => {
    setBusy('setup');
    try {
      const r = await api.post('/auth/2fa/setup');
      setTwofaSetup(r.data);
    } catch { toast.error('Could not start 2FA setup'); }
    finally { setBusy(null); }
  };

  const verifyCode = async () => {
    if (!code || code.length < 6) { toast.error('Enter the 6-digit code'); return; }
    setBusy('verify');
    try {
      await api.post('/auth/2fa/verify', { code: code.trim() });
      toast.success('Two-factor authentication enabled');
      setTwofaSetup(null);
      setCode('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Invalid code'); }
    finally { setBusy(null); }
  };

  const disable = async () => {
    const c = window.prompt('Enter your current 6-digit code to disable 2FA:');
    if (!c) return;
    setBusy('disable');
    try {
      await api.post('/auth/2fa/disable', { code: c.trim() });
      toast.success('2FA disabled');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(null); }
  };

  const revoke = async (sid) => {
    if (!window.confirm('Sign out this device?')) return;
    try {
      await api.delete(`/auth/sessions/${encodeURIComponent(sid)}`);
      toast.success('Session revoked');
      await loadSessions();
    } catch { toast.error('Failed'); }
  };

  const copySecret = () => {
    if (!twofaSetup?.secret) return;
    navigator.clipboard.writeText(twofaSetup.secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  return (
    <div className="min-h-screen bg-white pb-16" data-testid="login-security-page">
      <header className="px-5 pt-6 pb-4 flex items-center gap-3">
        <button onClick={() => navigate(-1)} data-testid="ls-back" aria-label="Back"
          className="w-9 h-9 rounded-full hover:bg-black/[0.04] flex items-center justify-center text-[#4B4B52]">
          <ArrowLeft size={18} />
        </button>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[1.4px] text-[#8A8A93]">SECURITY</p>
          <h1 className="font-brand text-[26px] font-[500] tracking-tight text-[#0F0F10] leading-tight">Login & Security</h1>
        </div>
      </header>

      <div className="max-w-xl mx-auto px-5 space-y-6">
        {/* 2FA */}
        <section className="rounded-2xl border border-[var(--hairline)] p-5 bg-white" data-testid="ls-2fa-block">
          <div className="flex items-start gap-3 mb-3">
            <ShieldCheck size={22} className="text-[#15803D] flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="font-brand text-[19px] font-[500] tracking-tight text-[#0F0F10] mb-1">Two-factor authentication</h2>
              <p className="text-[13px] text-[#4B4B52] leading-relaxed">
                Scan a QR with any authenticator app (Google Authenticator, 1Password, Authy) and enter the 6-digit code to enable.
              </p>
            </div>
          </div>

          {!twofaSetup ? (
            <div className="flex gap-2">
              <button onClick={startSetup} disabled={busy === 'setup'}
                data-testid="ls-2fa-setup-btn"
                className="flex-1 py-2.5 rounded-full bg-[#0F0F10] hover:bg-black text-white text-sm font-semibold disabled:opacity-50">
                {busy === 'setup' ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Set up 2FA'}
              </button>
              <button onClick={disable} disabled={busy === 'disable'}
                data-testid="ls-2fa-disable-btn"
                className="py-2.5 px-4 rounded-full border border-[var(--hairline-strong)] text-sm font-semibold text-[#4B4B52]">
                Disable
              </button>
            </div>
          ) : (
            <div className="space-y-3" data-testid="ls-2fa-setup-panel">
              <div className="p-3 rounded-xl bg-black/[0.03] border border-[var(--hairline)]">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-[#8A8A93] mb-1">Manual entry secret</p>
                <div className="flex items-center gap-2">
                  <code className="text-[13px] font-mono text-[#0F0F10] break-all flex-1">{twofaSetup.secret}</code>
                  <button onClick={copySecret} className="w-8 h-8 rounded-full hover:bg-black/[0.05] flex items-center justify-center">
                    {copied ? <Check size={14} className="text-[#15803D]" /> : <Copy size={14} />}
                  </button>
                </div>
                <p className="text-[11px] text-[#8A8A93] mt-1.5">Or scan the otpauth URI in your app.</p>
              </div>
              <input
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                data-testid="ls-2fa-code-input"
                placeholder="123456"
                className="w-full px-4 py-3 rounded-lg border border-[var(--hairline-strong)] text-center text-[19px] tracking-[6px] tabular-nums font-mono focus:outline-none focus:border-[#B91C1C]"
              />
              <div className="flex gap-2">
                <button onClick={verifyCode} disabled={busy === 'verify' || code.length !== 6}
                  data-testid="ls-2fa-verify-btn"
                  className="flex-1 py-2.5 rounded-full brand-gradient text-white text-sm font-semibold disabled:opacity-40">
                  {busy === 'verify' ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Verify & enable'}
                </button>
                <button onClick={() => { setTwofaSetup(null); setCode(''); }}
                  className="py-2.5 px-4 rounded-full border border-[var(--hairline-strong)] text-sm font-semibold text-[#4B4B52]">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </section>

        {/* Sessions */}
        <section data-testid="ls-sessions-block">
          <div className="flex items-center gap-2 mb-3 px-1">
            <Monitor size={16} className="text-[#0F0F10]" />
            <h2 className="font-brand text-[19px] font-[500] tracking-tight text-[#0F0F10]">Active sessions</h2>
          </div>
          <div className="rounded-2xl overflow-hidden border border-[var(--hairline)] bg-white">
            {sessions.length === 0 && (
              <p className="p-4 text-[13px] text-[#8A8A93] text-center">No active sessions.</p>
            )}
            {sessions.map((s, i) => (
              <div key={s.session_id} className="flex items-center gap-3 px-4 py-3 border-b border-[var(--hairline)] last:border-b-0"
                data-testid={`ls-session-${i}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-semibold text-[#0F0F10] truncate">
                    {s.device}
                    {s.current && <span className="ml-2 text-[10px] uppercase tracking-wider text-[#15803D] font-bold">· Current</span>}
                  </p>
                  <p className="text-[11px] text-[#8A8A93]">
                    {[s.city, s.ip].filter(Boolean).join(' · ')} · Last seen {s.last_seen_at ? new Date(s.last_seen_at).toLocaleString() : 'unknown'}
                  </p>
                </div>
                {!s.current && (
                  <button onClick={() => revoke(s.session_id)}
                    data-testid={`ls-session-revoke-${i}`}
                    className="p-2 rounded-full hover:bg-[#FEF2F2] text-[#B91C1C]">
                    <LogOut size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
